// How an answer's blocks are laid out (the inline-widget contract, 17 Oct 2026). An answer saved with `extras.layout = "inline-1"` (or a
// live turn streamed under the contract) is prose: its content blocks are drawn only where the text places their marker, its tail (next
// steps, follow-ups, links) follows the answer, and its progress (the plan, the team) never enters the body. Every other answer keeps the
// earlier layout: all its blocks go to the host's `renderBlocks`, unchanged. Pure, so it is tested on its own; and the stable keys that let
// a live answer become its saved form without remounting.
import { hasWidgetMarkers, INLINE_LAYOUT, normaliseWidgetMarkers, widgetIdsIn } from "@ncp/ui/lib/inlineWidgets";
import type { AnswerBlock, BlockPlacement, MessageExtras } from "./types";

const TAIL_KINDS = new Set(["next-steps", "links", "follow-ups"]);
const PROGRESS_KINDS = new Set(["plan", "team"]);
/** The kinds a marker never places (the server's `InlineWidgets.Placeable`): the plan and the tail. */
const NEVER_PLACED = new Set(["plan", ...TAIL_KINDS]);

/** The most content widgets one answer places (a draft is the deliverable and does not count), as the server checks. */
export const MAX_PLACED = 2;

/** A block's placement: as the server streamed or saved it, else by its kind (next steps, follow-ups and links follow; the plan and the team are work). */
export function placementOf(block: AnswerBlock): BlockPlacement {
  const p = block.placement;
  if (p === "inline" || p === "progress" || p === "tail") return p;
  const kind = String(block.type ?? "");
  return TAIL_KINDS.has(kind) ? "tail" : PROGRESS_KINDS.has(kind) ? "progress" : "inline";
}

/** Whether a saved answer places its widgets in its text. */
export function isInlineMessage(m: { extras?: MessageExtras | null } | null | undefined): boolean {
  return m?.extras?.layout === INLINE_LAYOUT;
}

/**
 * Whether a live turn streams under the inline contract. The server says so on its `start` frame (`layout: "inline-1"`; `null` or another
 * value is the earlier layout), and only a host that draws inline widgets (`renderInlineBlock`) takes it: every other host keeps the earlier
 * layout whatever the stream says. A `start` frame alone, or a block's `placement`, says nothing (every turn streams them). Until a server
 * names the layout on its start frame (`layout` absent), the first marker in the text tells: markers are only offered to a model that
 * writes inline widgets.
 */
export function isInlineLive(turn: { layout?: string | null; content?: string | null } | null | undefined, hostDrawsInline = true): boolean {
  if (!turn || !hostDrawsInline) return false;
  if (turn.layout === INLINE_LAYOUT) return true;
  return turn.layout === undefined && hasWidgetMarkers(turn.content);
}

/** Whether a block is one a marker may place (content, a draft, the team; never the plan or the tail). */
function placeable(b: AnswerBlock): boolean {
  const kind = String(b.type ?? "");
  return kind !== "" && !NEVER_PLACED.has(kind) && placementOf(b) !== "tail";
}

/**
 * The server's checks on an answer's markers, asked once per marker in text order (`normaliseWidgetMarkers`): the id names a block the
 * answer has, one a marker may place, not placed already, and at most {@link MAX_PLACED} besides drafts. While an answer streams the text is
 * drawn as the server will save it: a repeated id, a third widget or an invented id draws nothing (never a widget that later disappears).
 */
export function markerAcceptor(byId: ReadonlyMap<string, AnswerBlock>): (id: string) => boolean {
  const placed = new Set<string>();
  let counted = 0;
  return (id) => {
    const b = byId.get(id);
    if (!b || !placeable(b) || placed.has(id)) return false;
    if (b.type !== "draft") {
      if (counted >= MAX_PLACED) return false;
      counted++;
    }
    placed.add(id);
    return true;
  };
}

export interface AnswerLayout {
  /** The answer is prose with inline widgets. */
  inline: boolean;
  /** The text to draw: an inline answer's with its markers checked and placed as the server places them; any other answer's as it was. */
  content: string;
  /** What goes to the host's `renderBlocks` after the text: every block (the earlier layout), or the tail alone. */
  after: AnswerBlock[];
  /** The blocks a marker may place, by id (inline only: content and progress; the tail is never drawn in the body). */
  byId: Map<string, AnswerBlock>;
  /** The ids the text places that a block answers, in text order. */
  placed: string[];
  /** The work in progress (the plan, the team unless placed): the live steps line reads it. */
  progress: AnswerBlock[];
}

/**
 * Splits an answer's blocks between its text (by marker), its tail and its progress, and checks its markers as the server does (see
 * {@link markerAcceptor}); an answer of the earlier layout is left as it was.
 */
export function answerLayout(content: string | null | undefined, blocks: AnswerBlock[] | null | undefined, inline: boolean): AnswerLayout {
  const all = blocks ?? [];
  if (!inline) return { inline: false, content: content ?? "", after: all, byId: new Map(), placed: [], progress: [] };
  const byId = new Map<string, AnswerBlock>();
  const after: AnswerBlock[] = [];
  for (const b of all) {
    if (placementOf(b) === "tail") after.push(b);
    else if (b.id) byId.set(b.id, b);
  }
  const text = normaliseWidgetMarkers(content ?? "", markerAcceptor(byId));
  const placed = widgetIdsIn(text).filter((id) => byId.has(id));
  const progress = all.filter((b) => placementOf(b) === "progress" && !(b.id && placed.includes(b.id)));
  return { inline: true, content: text, after, byId, placed, progress };
}

/**
 * The React keys of a thread's answers: a live answer is keyed by its turn (`turn-<startedAt>`), and once the server names its saved id
 * (`start`, or `final`) the saved message is drawn under the same key — as is the question before it — so the swap from live to saved
 * remounts nothing (no chart re-measured, no widget state lost). One per thread.
 */
export class AnswerKeys {
  private readonly aliases = new Map<string, string>();

  /** Records that the saved message `messageId` is the live turn keyed `key`. */
  note(messageId: string | null | undefined, key: string): void {
    if (messageId && !this.aliases.has(messageId)) this.aliases.set(messageId, key);
  }

  /** The key of a saved answer. */
  answer(messageId: string): string {
    return this.aliases.get(messageId) ?? messageId;
  }

  /** The key of a saved question, given the answer that follows it (the live turn's question shares its turn's key). */
  question(messageId: string, nextAnswerId: string | null | undefined): string {
    const alias = nextAnswerId ? this.aliases.get(nextAnswerId) : undefined;
    return alias ? questionKey(alias) : messageId;
  }

  clear(): void {
    this.aliases.clear();
  }
}

/**
 * The keys of one drawing of a thread's entries. While the live turn is still drawn, its saved copy (should the thread already hold it)
 * keeps its own id as its key, and so does its question: two entries never share a key. Every other answer and question takes the key
 * `keys` holds for it.
 */
export function entryKeys(keys: AnswerKeys, liveId: string | null | undefined): { answer: (id: string) => string; question: (id: string, nextAnswerId: string | null | undefined) => string } {
  return {
    answer: (id) => (liveId && id === liveId ? id : keys.answer(id)),
    question: (id, next) => (liveId && next && next === liveId ? id : keys.question(id, next)),
  };
}

/** The key of a live turn's question. */
export function questionKey(turnKey: string): string {
  return `${turnKey}:q`;
}
